import { AioExternalReplacements, AioReplacement, updateExternal } from "aic";
import { hasStylesToRemove, removeStylesFromEditorV3 } from "editor-v3";
import { STYLES_TO_CLEAN, STYLES_TO_DELETE } from "../../../config";
import { shouldMergeExternal } from "./shouldMergeExternal";

/**
 * Result of removing styles from replacement lists.
 *
 * @template T - Content type for replacement texts
 */
export interface RemoveStylesResult<T> {
  /** Processed replacements with styles removed, or undefined if no replacements provided */
  processedReplacements?: AioReplacement<T>[];
  /** Whether any styles were found and removed during processing */
  hadStyles: boolean;
}

/**
 * Removes Optional and Notes styles from TLF table replacement lists recursively.
 * This function processes internal replacements (`AioReplacement`) and differs from
 * `removeStylesFromExternalReplacements` (hierarchy context) in several key ways:
 * - Merges external lists conditionally based on style presence
 * - Clears `externalName` when styles are removed to break hierarchy links
 * - Returns metadata about whether styles were found
 * - Handles `AioReplacement` structure (with airid, oldText, externalName)
 *
 * @template T - Content type for replacement texts
 * @param replacements - Optional internal replacements to process
 * @param externalLists - Optional external list replacements to conditionally merge
 * @returns Object containing processed replacements and flag indicating if styles were found
 */
export const removeStylesFromReplacementLists = <T>(
  replacements?: AioReplacement<T>[],
  externalLists?: AioExternalReplacements<T>[],
): RemoveStylesResult<T> => {
  if (!replacements) {
    return { processedReplacements: undefined, hadStyles: false };
  }

  let replacementHadStyles = false;

  const processedReplacements = replacements.map((replacement) => {
    // Use external list if it has styles, otherwise keep internal replacement
    const mergedReplacement = shouldMergeExternal(replacement, externalLists)
      ? updateExternal(replacement, externalLists)
      : replacement;

    // Remove styles from all texts in the replacement and recursively process subLists.
    let hadStylesToRemove = false;
    const processedTexts = mergedReplacement.newTexts.map((newText) => {
      const processedInnerTexts = newText.texts.map((text) => {
        if (hasStylesToRemove(text, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN])) {
          hadStylesToRemove = true;
        }
        return removeStylesFromEditorV3(text, STYLES_TO_DELETE, STYLES_TO_CLEAN);
      });

      // Recursively process subLists if they exist
      const { processedReplacements: processedSubLists, hadStyles: subListsHadStyles } =
        removeStylesFromReplacementLists(newText.subLists, externalLists);

      // Track if nested subLists had styles
      if (subListsHadStyles) {
        hadStylesToRemove = true;
      }

      return {
        ...newText,
        texts: processedInnerTexts,
        subLists: processedSubLists,
      };
    });

    // Track if any replacement in the array had styles
    replacementHadStyles ||= hadStylesToRemove;

    return {
      ...mergedReplacement,
      newTexts: processedTexts,
      externalName: hadStylesToRemove ? undefined : mergedReplacement.externalName,
    };
  });

  return {
    processedReplacements,
    hadStyles: replacementHadStyles,
  };
};
